import type { ReactNode } from 'react';
import { Icon, cn, type IconName } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 26 Oct 2026 · what came of something the person just did, given a page.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **NOT IN THE MOCKUP, SO THE DESIGN SYSTEM DECIDES IT.** The mockup's answer to a finished
 * connect is a toast; a return from somebody else's consent screen wanted more than one line that
 * vanishes. The shape is `Empty`'s — a glyph tile, a line, a sentence, the action — because the
 * design system allows a 20px glyph and never a picture, and a result that looked unlike an empty
 * state would look unlike the product. What it adds is a **tone**: the tile takes the status hue's
 * soft fill and text colour, so success, a fixable mistake and a failure read apart at a glance.
 *
 * ⚠️ **Domain-free.** It takes copy, a tone and the controls; the screen decides what happened.
 * Beside `card.tsx` rather than in `@forecastify/ui` for `Card`'s reason — one caller, no state.
 *
 * ⚠️ **The title is an `h2` at `--text-h2` (600 15px/22px)** — one step up from `Empty`'s 13px,
 * because here it is the whole pane's content rather than a gap in a list.
 */
export type OutcomeTone = 'ok' | 'warn' | 'bad';

const TONE: Record<OutcomeTone, { readonly icon: IconName; readonly tile: string }> = {
  ok: { icon: 'circle-check', tile: 'bg-status-ok-soft text-status-ok-text' },
  warn: { icon: 'triangle-alert', tile: 'bg-status-warn-soft text-status-warn-text' },
  bad: { icon: 'x', tile: 'bg-status-bad-soft text-status-bad-text' },
};

export function Outcome({
  tone,
  title,
  body,
  children,
  actions,
  marker,
}: {
  readonly tone: OutcomeTone;
  /** Already translated. */
  readonly title: string;
  /** The sentence under it. Already translated. */
  readonly body?: ReactNode;
  /** Anything the outcome needs shown beside the sentence. */
  readonly children?: ReactNode;
  /** The buttons — the one that resolves it first. */
  readonly actions?: ReactNode;
  /** A `data-outcome` value, so a test can tell which outcome rendered without reading copy. */
  readonly marker?: string;
}) {
  const style = TONE[tone];
  return (
    <div
      /* ⚠️ A failure interrupts; a success is a status. Read aloud on arrival either way, because
         the person was just on another site and this is the first thing they meet back here. */
      role={tone === 'ok' ? 'status' : 'alert'}
      data-outcome={marker}
      className="mx-auto flex max-w-[460px] flex-col items-center gap-3 px-6 py-14 text-center"
    >
      <span className={cn('grid size-11 place-items-center rounded-lg', style.tile)}>
        <Icon name={style.icon} size={20} />
      </span>
      <h2 className="text-[15px] leading-[22px] font-semibold">{title}</h2>
      {body === undefined ? null : (
        <p className="text-muted-foreground text-[13px] leading-relaxed">{body}</p>
      )}
      {children === undefined ? null : <div className="w-full">{children}</div>}
      {actions === undefined ? null : (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      )}
    </div>
  );
}
